import { useEffect, useMemo, useRef } from 'react';
import axiosInstance from '../fetchConfig/axiosConfig';
import { processCompRow } from './useStreamProcessorNew';
import { usePersistedDataStore } from '../components/store';
import logger from '../utils/logger';
import { idKey, normalizeCompId, findCompIndex } from '../components/CaseReview/common/compSheetLookup';

export function clearCompLookupCache() {
  resetAll();
  usePersistedDataStore.getState().incrementCompLookupCacheInvalidation?.();
}

const DISABLE_COMP_LOOKUPS = false;

// ── Batching policy ──────────────────────────────────────────────────────────
// One request = up to BATCH_SIZE unique comps for one tax year. Partial batches wait for a quiet
// period (debounce) but never longer than BATCH_MAX_WAIT_MS, so comps that trickle in case-by-case
// from the stream coalesce into a few requests instead of one tiny request per case.
const BATCH_SIZE = 40;
const BATCH_DEBOUNCE_MS = 1600; // just over the longest gap between streamed results
const BATCH_MAX_WAIT_MS = 4000; // a partial batch never waits longer than this
// Requests in flight per browser tab. Each one occupies a gunicorn thread + a DB connection.
const HARD_MAX_CONCURRENT = 2;

// Retries are only for failed requests / per-key server errors, with backoff.
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 500;
// "Not in inventory" gets one delayed recheck (covers a table reload window), then is remembered.
const MISS_RECHECKS = 1;
const MISS_RECHECK_DELAY_MS = 5_000;
const MISS_TTL_MS = 5 * 60_000;

function lookupCacheKey({ pid, transactionId }) {
  if (pid != null && pid !== '') return `p:${pid}`;
  if (transactionId != null && transactionId !== '') return `t:${transactionId}`;
  return null;
}

// Cache / job identity includes the tax year: the inventory SP is year-specific.
function entryKey(taxYear, lookupKey) {
  return `${taxYear}::${lookupKey}`;
}

/**
 * Single shared engine: every `useCompLookups()` caller shares one job table, queue and cache.
 * One job per unique (year, comp); every case that needs that comp subscribes to the same job.
 */
const shared = {
  cache: new Map(),   // entryKey -> normalized row
  misses: new Map(),  // entryKey -> expiresAt (confirmed not found)
  jobs: new Map(),    // entryKey -> job
  queue: [],          // entryKeys waiting to be sent (FIFO)
  inFlight: 0,
  debounceTimer: null,
  maxWaitTimer: null,
  retryTimer: null,
  maxConcurrent: HARD_MAX_CONCURRENT,
};

function expectedSubjectPidForCase(negotiationObj, caseIndex) {
  if (typeof caseIndex !== 'number' || caseIndex < 0) return undefined;
  const fromUids = negotiationObj?.uids?.[caseIndex];
  if (fromUids) return fromUids;
  return negotiationObj?.cases?.[caseIndex]?.parcel_id;
}

function getStreamGuardResult(target) {
  const ref = target?.ref;
  const caseIndex = target?.caseIndex;
  if (!ref?.properties || typeof caseIndex !== 'number' || caseIndex < 0) {
    return { ok: false, reason: 'missing_ref_or_bad_caseIndex', caseIndex };
  }
  if (Array.isArray(ref.updated) && ref.updated[caseIndex] !== true) {
    return { ok: false, reason: 'stream_not_updated_yet', caseIndex, updatedAtIndex: ref.updated?.[caseIndex] };
  }
  const regular = ref.properties?.[0]?.[caseIndex];
  const pidArr = regular?.parcel_id ?? regular?.ParcelID;
  const subjectPid = Array.isArray(pidArr) ? pidArr[0] : undefined;
  if (!regular) return { ok: false, reason: 'no_regular_row_yet', caseIndex };
  if (!Array.isArray(pidArr)) return { ok: false, reason: 'missing_subject_pid_array', caseIndex, subjectPid };
  if (!subjectPid) return { ok: false, reason: 'missing_subject_pid_value', caseIndex, subjectPid };
  const expectedSubject = expectedSubjectPidForCase(target?.negotiationObj, caseIndex);
  if (expectedSubject && subjectPid !== expectedSubject) {
    return {
      ok: false,
      reason: 'subject_pid_mismatch_stale_ref',
      caseIndex,
      subjectPid,
      expectedSubjectPid: expectedSubject,
    };
  }
  // Stream complete (updated=true) is enough; KNN may return only the subject.
  return { ok: true, reason: 'ok', caseIndex, subjectPid };
}

function mergeIntoRef(row, { ref, caseIndex, negotiationObj, insertIndex = null, panes = [0, 1], reportType, transactionId = null }) {
  if (!row || !ref?.properties) return;

  const extractSubjectValues = target => {
    if (!target?.parcel_id?.length) return null;
    const fields = ['IFMV', 'Sqft', 'ConditionIn', 'Grade', 'Bath', 'HalfBath', 'TotalGarageCap', 'Acres'];
    return fields.reduce((acc, f) => {
      if (target[f]?.[0] !== undefined) acc[f] = target[f][0];
      return acc;
    }, {});
  };

  // When we looked up by transaction id, force that id onto the row (SP may omit/alter it)
  if (transactionId != null && transactionId !== '') {
    row = { ...row, TransactionID: transactionId };
  }

  const rt = reportType || negotiationObj?.reportType;
  const lookupKey = idKey(rt);
  const compId = lookupKey === 'TransactionID'
    ? (row.TransactionID ?? transactionId)
    : (row.parcel_id ?? row.ParcelID ?? '');

  (panes || [0, 1]).forEach((pane) => {
    const target = ref.properties?.[pane]?.[caseIndex];
    if (!target?.parcel_id) return;
    if (findCompIndex(target, compId, rt) >= 0) return;
    processCompRow(row, target, negotiationObj, extractSubjectValues(target), insertIndex);
  });
}

// ── Cache helpers ────────────────────────────────────────────────────────────

function isKnownMiss(key) {
  const expiresAt = shared.misses.get(key);
  if (expiresAt == null) return false;
  if (expiresAt > Date.now()) return true;
  shared.misses.delete(key);
  return false;
}

function fanOut(job, row) {
  job.subscribers.forEach((sub) => {
    try {
      mergeIntoRef(row, { ...sub.target, reportType: sub.reportType, transactionId: sub.transactionId });
    } catch (e) {
      logger.error('[CompLookup] merge error', { key: job.key, caseIndex: sub.target?.caseIndex, message: e?.message });
    }
  });
}

// ── Scheduling ───────────────────────────────────────────────────────────────

function queuedJobs() {
  const out = [];
  shared.queue.forEach((key) => {
    const job = shared.jobs.get(key);
    if (job?.queued) out.push(job);
  });
  return out;
}

function readyCount(now = Date.now()) {
  return queuedJobs().filter(j => j.notBefore <= now).length;
}

function clearTimers() {
  clearTimeout(shared.debounceTimer);
  clearTimeout(shared.maxWaitTimer);
  clearTimeout(shared.retryTimer);
  shared.debounceTimer = null;
  shared.maxWaitTimer = null;
  shared.retryTimer = null;
}

function wakeForRetries() {
  clearTimeout(shared.retryTimer);
  shared.retryTimer = null;
  const now = Date.now();
  let next = Infinity;
  queuedJobs().forEach((j) => {
    if (j.notBefore > now) next = Math.min(next, j.notBefore);
  });
  if (next !== Infinity) shared.retryTimer = setTimeout(schedule, next - now);
}

function schedule() {
  const ready = readyCount();
  if (ready >= BATCH_SIZE) {
    pump();
    return;
  }
  if (ready > 0) {
    clearTimeout(shared.debounceTimer);
    shared.debounceTimer = setTimeout(pump, BATCH_DEBOUNCE_MS);
    if (!shared.maxWaitTimer) shared.maxWaitTimer = setTimeout(pump, BATCH_MAX_WAIT_MS);
  }
  wakeForRetries();
}

// Pull up to BATCH_SIZE ready jobs that share one tax year; everything else stays queued in order.
function takeBatch(now) {
  let year = null;
  const picked = [];
  const rest = [];
  shared.queue.forEach((key) => {
    const job = shared.jobs.get(key);
    if (!job?.queued) return; // stale entry (cleared or already taken)
    const fits = picked.length < BATCH_SIZE && job.notBefore <= now && (year === null || job.taxYear === year);
    if (fits) {
      year = job.taxYear;
      job.queued = false;
      job.inFlight = true;
      picked.push(job);
    } else {
      rest.push(key);
    }
  });
  shared.queue = rest;
  return picked.length ? { year, jobs: picked } : null;
}

function pump() {
  clearTimeout(shared.debounceTimer);
  clearTimeout(shared.maxWaitTimer);
  shared.debounceTimer = null;
  shared.maxWaitTimer = null;
  const now = Date.now();
  while (shared.inFlight < shared.maxConcurrent) {
    const batch = takeBatch(now);
    if (!batch) break;
    sendBatch(batch);
  }
  wakeForRetries();
}

function requeue(job, delayMs) {
  job.inFlight = false;
  job.queued = true;
  job.notBefore = Date.now() + delayMs;
  shared.queue.push(job.key);
}

async function sendBatch({ year, jobs }) {
  shared.inFlight += 1; // runs synchronously, before the first await
  let data = null;
  try {
    const res = await axiosInstance.post('/lookup_property_inventory_batch', {
      lookups: jobs.map(j => ({ key: j.lookupKey, parcel_id: j.pid ?? null, transaction_id: j.transactionId ?? null })),
      year,
    });
    data = res?.data || {};
  } catch (err) {
    logger.error('[CompLookup] lookup_property_inventory_batch error', { year, count: jobs.length, message: err?.message });
  } finally {
    shared.inFlight -= 1;
  }

  jobs.forEach((job) => {
    if (shared.jobs.get(job.key) !== job) return; // cache was invalidated while this was in flight
    const value = data ? data[job.lookupKey] : undefined;

    if (Array.isArray(value)) {
      const row = normalizeCompRow(value[0]) ?? null;
      if (row) {
        shared.cache.set(job.key, row);
        shared.jobs.delete(job.key);
        fanOut(job, row);
      } else if (job.missChecks < MISS_RECHECKS) {
        job.missChecks += 1;
        job.recheck = true;
        requeue(job, MISS_RECHECK_DELAY_MS);
      } else {
        shared.misses.set(job.key, Date.now() + MISS_TTL_MS);
        shared.jobs.delete(job.key);
      }
      return;
    }

    // Request failed, key missing from the response, or the server returned null (per-key error).
    if (job.attempts < MAX_RETRIES) {
      job.attempts += 1;
      const backoff = RETRY_BASE_MS * 2 ** (job.attempts - 1);
      requeue(job, backoff + Math.floor(Math.random() * backoff));
    } else {
      shared.jobs.delete(job.key);
      logger.warn(`[CompLookup] Dropping ${job.pid != null ? `pid=${job.pid}` : `transactionId=${job.transactionId}`} after ${MAX_RETRIES} retries`, {
        year: job.taxYear,
        subscribers: job.subscribers.size,
      });
    }
  });

  schedule();
}

// ── Public API ───────────────────────────────────────────────────────────────

function enqueueLookup({ pid = null, transactionId = null, taxYear, signal, target, reportType }) { // eslint-disable-line no-unused-vars
  const lookupKey = lookupCacheKey({ pid, transactionId });
  if (!lookupKey || !target?.ref || typeof target.caseIndex !== 'number') return;
  if (!getStreamGuardResult(target).ok) return;

  const key = entryKey(taxYear, lookupKey);
  const hit = shared.cache.get(key);
  if (hit) {
    setTimeout(() => mergeIntoRef(hit, { ...target, reportType, transactionId }), 0);
    return;
  }
  if (isKnownMiss(key)) return;

  let job = shared.jobs.get(key);
  if (!job) {
    job = {
      key, lookupKey, taxYear, pid, transactionId,
      subscribers: new Map(), // caseIndex -> { target, reportType, transactionId }
      attempts: 0, missChecks: 0, recheck: false,
      notBefore: 0, queued: false, inFlight: false,
    };
    shared.jobs.set(key, job);
  }
  if (!job.subscribers.has(target.caseIndex)) {
    job.subscribers.set(target.caseIndex, { target, reportType, transactionId });
  }
  if (!job.queued && !job.inFlight) {
    job.queued = true;
    job.notBefore = 0;
    shared.queue.push(key);
    schedule();
  }
}

function enqueueMissingSavedComps({
  savedCompIds = [], existingCompIds = [], taxYear, signal,
  ref, caseIndex, negotiationObj, insertIndex = null, panes = [0, 1],
  reportType,
}) {
  const rt = reportType || negotiationObj?.reportType || 'standard';
  const key = idKey(rt);
  const existing = new Set(
    existingCompIds.map(normalizeCompId).filter(Boolean),
  );
  const missing = savedCompIds.filter(
    (id) => id != null && id !== '' && !existing.has(normalizeCompId(id)),
  );

  missing.forEach((id) => {
    const byTxn = key !== 'parcel_id';
    enqueueLookup({
      pid: byTxn ? null : id,
      transactionId: byTxn ? id : null,
      taxYear,
      signal,
      reportType: rt,
      target: { ref, caseIndex, negotiationObj, insertIndex, panes },
    });
  });
  return missing.length;
}

function getStatus() {
  return { cacheSize: shared.cache.size, pending: queuedJobs().length, running: shared.inFlight };
}

// Jobs only waiting on a delayed "not found" recheck don't block flush(); they still merge in later.
function hasActiveWork() {
  return shared.inFlight > 0 || queuedJobs().some(j => !j.recheck);
}

function clearQueue() {
  queuedJobs().forEach(j => shared.jobs.delete(j.key));
  shared.queue = [];
  clearTimers();
}

function clearCache() {
  shared.cache.clear();
  shared.misses.clear();
}

function resetAll() {
  clearTimers();
  shared.queue = [];
  shared.jobs.clear(); // in-flight results are discarded by the identity check in sendBatch
  clearCache();
}

function flush(onComplete, timeoutMs = 15_000) {
  pump();
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const check = () => {
      if (!hasActiveWork()) {
        onComplete?.();
        resolve();
      } else if (Date.now() > deadline) {
        logger.warn('[CompLookup] flush() timed out — clearing queue');
        clearQueue();
        onComplete?.();
        resolve();
      } else {
        // Don't let the debounce slow a flush down.
        if (shared.inFlight < shared.maxConcurrent && readyCount() > 0) pump();
        setTimeout(check, 100);
      }
    };
    setTimeout(check, 100);
  });
}

function getQueueStats() {
  const jobs = queuedJobs();
  return {
    total: jobs.length,
    byYear: Array.from(
      jobs.reduce((acc, j) => {
        acc.set(j.taxYear, (acc.get(j.taxYear) || 0) + 1);
        return acc;
      }, new Map()).entries(),
    ),
    canBatch: jobs.length >= BATCH_SIZE,
    isDraining: shared.inFlight > 0,
  };
}

/** Immediate send (used by useStreamProcessorNew after stream). */
function forceBatchProcess() {
  pump();
}

function processBatch() {
  forceBatchProcess();
}

function getBatchThreshold() {
  return BATCH_SIZE;
}

const sharedApi = {
  enqueueLookup,
  enqueueMissingSavedComps,
  getStatus,
  hasActiveWork,
  flush,
  clearQueue,
  clearCache,
  getQueueStats,
  forceBatchProcess,
  processBatch,
  getBatchThreshold,
  // Keys are now `${taxYear}::p:${pid}` / `${taxYear}::t:${txnId}`
  _cache: { get current() { return shared.cache; } },
};

const disabledApi = {
  enqueueLookup: () => {},
  enqueueMissingSavedComps: () => 0,
  getStatus: () => ({ cacheSize: 0, pending: 0, running: 0 }),
  hasActiveWork: () => false,
  flush: async () => {},
  clearQueue: () => {},
  clearCache: () => {},
  getQueueStats: () => ({}),
  forceBatchProcess: () => {},
  processBatch: () => {},
  getBatchThreshold: () => BATCH_SIZE,
  _cache: { current: new Map() },
};

/** Module-level flush for post-stream callers (e.g. autosave NAL check) that cannot use the hook. */
export const flushCompLookups = (onComplete, timeoutMs) =>
  (DISABLE_COMP_LOOKUPS ? disabledApi : sharedApi).flush(onComplete, timeoutMs);

export const hasActiveCompLookups = () =>
  (DISABLE_COMP_LOOKUPS ? disabledApi : sharedApi).hasActiveWork();

/**
 * Non-blocking comp lookup system (shared singleton — see `shared` above).
 * `maxConcurrent` is kept for API compatibility but capped at HARD_MAX_CONCURRENT.
 */
export function useCompLookups({ maxConcurrent = HARD_MAX_CONCURRENT } = {}) {
  useEffect(() => {
    shared.maxConcurrent = Math.min(HARD_MAX_CONCURRENT, Math.max(1, maxConcurrent));
  }, [maxConcurrent]);

  const cacheVersion = usePersistedDataStore(s => s.compLookupCacheInvalidationVersion);
  const versionRef = useRef(cacheVersion);
  useEffect(() => {
    if (cacheVersion !== versionRef.current && cacheVersion > 0) {
      versionRef.current = cacheVersion;
      clearCache();
    }
  }, [cacheVersion]);

  return useMemo(
    () => (DISABLE_COMP_LOOKUPS ? disabledApi : sharedApi),
    [],
  );
}

function normalizeCompRow(raw) {
  if (!raw) return raw;
  const parcel_id = raw.parcel_id ?? raw.ParcelID ?? raw.PID ?? '';
  const Latitude = raw.Latitude ?? raw.latitude ?? null;
  const Longitude = raw.Longitude ?? raw.longitude ?? null;
  const Bath = raw.Bath ?? raw.BathsFull ?? raw.fullBaths ?? '';
  const HalfBath = raw.HalfBath ?? raw.BathsHalf ?? raw.halfBaths ?? '';
  const TotalGarageCapacity = raw.TotalGarageCapacity ?? raw.GarageCapacity ?? '';
  const TransactionID = raw.TransactionID ?? raw.trans_id ?? null;
  return { ...raw, parcel_id, ParcelID: parcel_id, TransactionID, Latitude, Longitude, Bath, HalfBath, TotalGarageCapacity };
}
